import { createReducer } from '@reduxjs/toolkit'

import actionTypes from './actionTypes'

const calculateDeviceAge = enrolledDate => {
  if (!enrolledDate) return { deviceAge: '', deviceAgeMonths: -1 }
  const enrolled = new Date(enrolledDate)
  const now = new Date()
  let years = now.getFullYear() - enrolled.getFullYear()
  let months = now.getMonth() - enrolled.getMonth()
  if (months < 0) {
    years -= 1
    months += 12
  }
  const totalMonths = years * 12 + months
  const parts = []
  if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`)
  if (months > 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`)

  return {
    deviceAge: parts.length > 0 ? parts.join(', ') : 'Less than a month',
    deviceAgeMonths: totalMonths,
  }
}

/**
 * Normalise a raw API record from the device endpoint.
 * The response uses `name` for the device friendly name.
 * The `enrolledDate` is an ISO datetime — we display only the date part (YYYY-MM-DD).
 */
const normaliseDevice = raw => {
  const enrolledDate = raw.enrolledDate ? raw.enrolledDate.split('T')[0] : ''
  const { deviceAge, deviceAgeMonths } = calculateDeviceAge(enrolledDate)

  return {
    comments: raw.comments ?? '',
    deviceAge,
    deviceAgeMonths,
    deviceFriendlyName:
      raw.name ?? raw['device friendly name'] ?? raw.deviceFriendlyName ?? '',
    enrolledDate,
    hexnodeRefId: raw.hexnodeRefId ?? '',
    hologramIccid: raw.hologramIccid ?? '',
    hologramSimId: raw.hologramSimId ?? '',
    id: raw.id,
    imeiNumbers: raw.imeiNumbers ?? '',
    status: raw.status,
    vesselName: raw.vesselName ?? '',
  }
}

const filterDevices = (allDevices, searchQuery) => {
  if (!searchQuery || !searchQuery.trim()) {
    return allDevices
  }

  const query = searchQuery.toLowerCase().trim()

  return allDevices.filter(device => {
    const vesselName = (device.vesselName || '').toLowerCase()
    const friendlyName = (device.deviceFriendlyName || '').toLowerCase()

    return vesselName.includes(query) || friendlyName.includes(query)
  })
}

const initialState = {
  allDevices: [],
  devices: [],
  registerDeviceError: null,
  registeringDevice: false,
  requestingDevices: false,
  requestingShipOptions: false,
  searchQuery: '',
  shipOptions: [],
}

const deviceManagementReducer = createReducer(initialState, builder => {
  builder
    .addCase(actionTypes.SET_REQUESTING_DEVICES, (state, action) => {
      state.requestingDevices = action.payload
    })
    .addCase(actionTypes.DEVICES_REQUEST_SUCCESSFUL, (state, action) => {
      const allDevices = (action.payload || []).map(normaliseDevice)
      state.allDevices = allDevices
      state.devices = filterDevices(allDevices, state.searchQuery)
    })
    .addCase(actionTypes.DEVICES_REQUEST_UNSUCCESSFUL, state => {
      state.allDevices = []
      state.devices = []
    })
    .addCase(actionTypes.SET_DEVICE_SEARCH_QUERY, (state, action) => {
      state.searchQuery = action.payload
      state.devices = filterDevices(state.allDevices, action.payload)
    })

    // Register device
    .addCase(actionTypes.SET_REGISTERING_DEVICE, (state, action) => {
      state.registeringDevice = action.payload
    })
    .addCase(actionTypes.SET_REGISTER_DEVICE_ERROR, (state, action) => {
      state.registerDeviceError = action.payload
    })

    // Ship options
    .addCase(actionTypes.SET_REQUESTING_SHIP_OPTIONS, (state, action) => {
      state.requestingShipOptions = action.payload
    })
    .addCase(actionTypes.SHIP_OPTIONS_REQUEST_SUCCESSFUL, (state, action) => {
      const items = Array.isArray(action.payload)
        ? action.payload
        : [action.payload]
      state.shipOptions = items.map(item => ({
        id: item.id,
        name: item.vesselName,
      }))
    })
    .addCase(actionTypes.SHIP_OPTIONS_REQUEST_UNSUCCESSFUL, state => {
      state.shipOptions = []
    })

    .addDefaultCase(state => state)
})

export default deviceManagementReducer
